export type WorkHoursCoverage = 'covered' | 'not_covered' | 'unknown';

/**
 * Hours coverage is only actual worked vs expected.
 * Early exit / late arrival must not decide this.
 */
export function resolveWorkHoursCoverage(
  workedMinutes: number | null | undefined,
  expectedWorkMinutes: number | null | undefined,
): WorkHoursCoverage {
  if (
    typeof workedMinutes !== 'number' ||
    !Number.isFinite(workedMinutes) ||
    typeof expectedWorkMinutes !== 'number' ||
    !Number.isFinite(expectedWorkMinutes) ||
    expectedWorkMinutes <= 0
  ) {
    return 'unknown';
  }
  return workedMinutes >= expectedWorkMinutes ? 'covered' : 'not_covered';
}

/** Covered / Not Covered only once the session is checked out. */
export function resolveCheckedOutWorkHoursCoverage(
  isCheckedOut: boolean,
  workedMinutes: number | null | undefined,
  expectedWorkMinutes: number | null | undefined,
): WorkHoursCoverage {
  if (!isCheckedOut) return 'unknown';
  return resolveWorkHoursCoverage(workedMinutes, expectedWorkMinutes);
}

export function shortHoursMinutesFromCoverage(
  workedMinutes: number | null | undefined,
  expectedWorkMinutes: number | null | undefined,
): number {
  if (resolveIncompleteWorkKind(workedMinutes, expectedWorkMinutes) !== 'short_hours') {
    return 0;
  }
  return Math.max(0, (expectedWorkMinutes as number) - (workedMinutes as number));
}

/** Half-day hours are half of the employee's live Expected Work Hours from the API. */
export function halfDayWorkMinutes(
  expectedWorkMinutes: number | null | undefined,
): number | null {
  if (
    typeof expectedWorkMinutes !== 'number' ||
    !Number.isFinite(expectedWorkMinutes) ||
    expectedWorkMinutes <= 0
  ) {
    return null;
  }
  return Math.floor(expectedWorkMinutes / 2);
}

export type IncompleteWorkKind =
  | 'covered'
  | 'short_hours'
  | 'half_day_absent'
  | 'unknown';

/**
 * After checkout, incomplete days split at the half-day mark:
 * worked <= half of expected → Half-Day Absent
 * half < worked < expected → Short Hours
 */
export function resolveIncompleteWorkKind(
  workedMinutes: number | null | undefined,
  expectedWorkMinutes: number | null | undefined,
): IncompleteWorkKind {
  const coverage = resolveWorkHoursCoverage(
    workedMinutes,
    expectedWorkMinutes,
  );
  if (coverage === 'unknown') return 'unknown';
  if (coverage === 'covered') return 'covered';
  const halfDayMinutes = halfDayWorkMinutes(expectedWorkMinutes);
  if (
    halfDayMinutes == null ||
    typeof workedMinutes !== 'number' ||
    workedMinutes <= halfDayMinutes
  ) {
    return 'half_day_absent';
  }
  return 'short_hours';
}

export function workHoursCoverageLabel(
  coverage: WorkHoursCoverage,
): 'Covered' | 'Not Covered' | null {
  if (coverage === 'covered') return 'Covered';
  if (coverage === 'not_covered') return 'Not Covered';
  return null;
}
